import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { GlassCard } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

interface PatchPolicy {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  schedule_type: string;
  created_at: string;
}

interface TagPatchPoliciesTabProps {
  tagId: string;
}

export const TagPatchPoliciesTab = ({ tagId }: TagPatchPoliciesTabProps) => {
  const [policies, setPolicies] = useState<PatchPolicy[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setIsLoading(true);
        
        // Get policy IDs assigned to this tag
        const { data: policyTagsData, error: policyTagsError } = await supabase
          .from("patch_policy_tags")
          .select("patch_policy_id")
          .eq("tag_id", tagId);

        if (policyTagsError) throw policyTagsError;

        const policyIds = policyTagsData?.map(pt => pt.patch_policy_id) || [];

        if (policyIds.length === 0) {
          setPolicies([]);
          return;
        }

        // Fetch the actual policies
        const { data: policiesData, error: policiesError } = await supabase
          .from("patch_policies")
          .select("*")
          .in("id", policyIds);

        if (policiesError) throw policiesError;

        setPolicies(policiesData || []);
      } catch (error) {
        console.error("Error fetching patch policies:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicies();
  }, [tagId]);

  const filteredPolicies = policies.filter(policy =>
    policy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (policy.description && policy.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search patch policies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredPolicies.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchQuery ? "No patch policies found matching your search" : "No patch policies assigned to this tag"}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filteredPolicies.map((policy) => (
            <Link key={policy.id} to={`/admin/patching`}>
              <GlassCard className="p-4 hover:shadow-md transition-shadow cursor-pointer h-full">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-foreground">{policy.name}</h3>
                  <Badge variant={policy.enabled ? "default" : "secondary"} className="text-xs">
                    {policy.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                {policy.description && (
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {policy.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="capitalize">{policy.schedule_type}</span>
                  <span>Created {new Date(policy.created_at).toLocaleDateString()}</span>
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
